import { ChallengeMode, ChallengeStatus, DonateChallenge } from '../api/payment/donate-challenge.api';

// Hàm thuần cho overlay OBS "thử thách donate" — đếm ngược, đổi trạng thái, tiến độ.
// Tách khỏi component để test không phải dựng React/SignalR.

/** Độ lệch đồng hồ máy so với server (ms), để đếm ngược không lệch khi máy sai giờ. */
export const clockSkewMs = (serverNowIso: string, clientNowMs: number = Date.now()): number =>
  new Date(serverNowIso).getTime() - clientNowMs;

/** "2 ngày 3 giờ" | "3 giờ 05 phút" | "04:59" (dưới 1 giờ, mm:ss) | "Đã hết giờ" khi ≤ 0. */
export const formatCountdown = (ms: number): string => {
  if (ms <= 0) return 'Đã hết giờ';

  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days >= 1) return `${days} ngày ${hours} giờ`;
  if (hours >= 1) return `${hours} giờ ${`${minutes}`.padStart(2, '0')} phút`;
  return `${`${minutes}`.padStart(2, '0')}:${`${seconds}`.padStart(2, '0')}`;
};

/**
 * True khi dữ liệu thử thách đang cầm chắc đã lỗi thời và cần tải lại SỚM, ngoài chu kỳ poll
 * thường: `Scheduled` đã qua giờ bắt đầu (chưa kịp chuyển `Running`) hoặc `Running` đã qua giờ
 * kết thúc (job chốt chạy mỗi phút chưa kịp tới hoặc chưa báo `Finalized`). Không có điều kiện
 * này, overlay/trang sự kiện đứng yên ở "Bắt đầu sau Đã hết giờ" cho tới vòng tải lại kế tiếp.
 */
export const needsRefresh = (c: DonateChallenge | null | undefined, nowMs: number): boolean => {
  if (!c || !c.exists) return false;
  if (c.status === 'Scheduled') return nowMs >= new Date(c.startAt).getTime();
  if (c.status === 'Running') return nowMs >= new Date(c.endAt).getTime();
  return false;
};

/**
 * Nhãn đếm ngược "tự nhiên": còn thời gian ⇒ số đếm ngược (kèm tiền tố "Bắt đầu sau "/"Còn "
 * nếu `withPrefix`); đã chạm 0 mà máy chủ CHƯA kịp báo trạng thái mới (lệch đồng hồ hoặc job
 * chốt/chuyển trạng thái chưa chạy tới) ⇒ câu tự nhiên, tránh lộ "Bắt đầu sau Đã hết giờ" /
 * "Còn Đã hết giờ" ra người xem.
 */
export const countdownLabel = (
  status: 'Scheduled' | 'Running',
  remainingMs: number,
  withPrefix: boolean = true
): string => {
  if (remainingMs > 0) {
    const value = formatCountdown(remainingMs);
    if (!withPrefix) return value;
    return status === 'Scheduled' ? `Bắt đầu sau ${value}` : `Còn ${value}`;
  }
  return status === 'Scheduled' ? 'Sắp bắt đầu' : 'Đang chốt kết quả';
};

const CHAMPION_WINDOW_MS = 10 * 60 * 1000;

/** Overlay: 'hidden' | 'scheduled' | 'running' | 'champion'. champion chỉ trong 10 phút sau finalizedAt; Cancelled/không tồn tại/quá 10 phút ⇒ hidden. */
export const overlayView = (
  c: DonateChallenge | null | undefined,
  nowMs: number
): 'hidden' | 'scheduled' | 'running' | 'champion' => {
  if (!c || !c.exists) return 'hidden';

  if (c.status === 'Scheduled') return 'scheduled';
  if (c.status === 'Running') return 'running';

  if (c.status === 'Finalized') {
    if (!c.finalizedAt) return 'hidden';
    const finalizedAtMs = new Date(c.finalizedAt).getTime();
    return nowMs - finalizedAtMs <= CHAMPION_WINDOW_MS ? 'champion' : 'hidden';
  }

  // Cancelled và mọi trạng thái khác.
  return 'hidden';
};

/** Phần trăm tiến độ về đích (0–100, kẹp trần). target null/0 ⇒ 0. */
export const raceProgress = (amount: number, target: number | null): number => {
  if (!target) return 0;
  return Math.max(0, Math.min(100, (amount / target) * 100));
};

/** 100000 ⇒ "100.000". */
export const formatCa = (v: number): string => Number(v || 0).toLocaleString('vi-VN');

/** ISO UTC ("2026-09-29T13:00:00Z") ⇒ giá trị cho <input type="datetime-local"> theo giờ máy người dùng. */
export const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/** Giá trị <input type="datetime-local"> (giờ máy) ⇒ ISO UTC gửi lên máy chủ. Rỗng/không hợp lệ ⇒ null. */
export const localInputToIso = (local?: string | null): string | null => {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** "dd/MM" theo giờ máy — dùng cho tiêu đề "Quán quân thử thách ngày dd/MM". */
export const formatDayMonth = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
};

/**
 * Quyết định `startAt` gửi lên khi SỬA một thử thách đang Scheduled.
 * BE hiểu `startAt=null` trên UPDATE là "giữ nguyên giờ bắt đầu cũ" — khác CREATE, nơi null
 * nghĩa là "bắt đầu ngay". Nên chỉ gửi giá trị mới khi người dùng THẬT SỰ đổi (đổi kiểu bắt
 * đầu, hoặc đổi giờ hẹn); giữ nguyên lựa chọn cũ thì gửi null để tránh vô tình đổi giờ.
 */
export const resolveUpdateStartAt = (args: {
  startMode: 'now' | 'schedule';
  startLocal: string;
  originalStartMode: 'now' | 'schedule';
  originalStartLocal: string;
  nowIso?: string;
}): string | null => {
  const { startMode, startLocal, originalStartMode, originalStartLocal } = args;
  const unchanged =
    startMode === originalStartMode && (startMode === 'now' || startLocal === originalStartLocal);
  if (unchanged) return null;
  if (startMode === 'now') return args.nowIso || new Date().toISOString();
  return localInputToIso(startLocal);
};

/** Nhãn + màu Tag cho trạng thái thử thách (trang admin "Sự kiện donate"). */
export const challengeStatusLabel = (status: ChallengeStatus): { label: string; color: string } => {
  switch (status) {
    case 'Scheduled':
      return { label: 'Sắp diễn ra', color: 'blue' };
    case 'Running':
      return { label: 'Đang diễn ra', color: 'green' };
    case 'Finalized':
      return { label: 'Đã kết thúc', color: 'default' };
    case 'Cancelled':
      return { label: 'Đã huỷ', color: 'red' };
    default:
      return { label: status, color: 'default' };
  }
};

/** Nhãn + màu Tag cho trạng thái trao quà của một người thắng (rewardStatus BE trả). */
export const rewardStatusLabel = (status: string): { label: string; color: string } => {
  switch (status) {
    case 'None':
      return { label: 'Chưa có quà', color: 'default' };
    case 'Pending':
      return { label: 'Đang chờ trao', color: 'gold' };
    case 'Granted':
      return { label: 'Đã trao', color: 'green' };
    case 'Failed':
      return { label: 'Lỗi', color: 'red' };
    default:
      return { label: status, color: 'default' };
  }
};

export interface RewardRangeInput {
  rankFrom: number;
  rankTo: number;
  storeItemCode?: string | null;
  durationDays?: number | null;
}

/**
 * Kiểm tra hợp lệ trước khi gửi lên máy chủ (đối chiếu lại việc BE validate): khoảng hạng
 * hợp lệ (từ ≥ 1, đến ≥ từ), không chồng nhau giữa các dòng, và số ngày 1–365 bắt buộc khi
 * dòng đó có tặng vật phẩm. Trả về câu lỗi tiếng Việt đầu tiên gặp phải, hoặc null nếu hợp lệ.
 */
export const validateRewardRows = (rows: RewardRangeInput[]): string | null => {
  if (rows.length === 0) return 'Cần ít nhất một dòng phần thưởng.';

  for (const r of rows) {
    if (!Number.isFinite(r.rankFrom) || !Number.isFinite(r.rankTo) || r.rankFrom < 1 || r.rankTo < r.rankFrom) {
      return 'Khoảng hạng không hợp lệ.';
    }
    if (r.storeItemCode && (!r.durationDays || r.durationDays < 1 || r.durationDays > 365)) {
      return 'Số ngày phải từ 1 đến 365 khi có tặng vật phẩm.';
    }
  }

  const sorted = [...rows].sort((a, b) => a.rankFrom - b.rankFrom);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].rankFrom <= sorted[i - 1].rankTo) return 'Các khoảng hạng không được chồng nhau.';
  }
  return null;
};

/** Câu luật một dòng hiển thị cho người xem (thẻ trang cá nhân + khối cài đặt). */
export const challengeRuleSentence = (
  mode: ChallengeMode,
  targetAmount: number | null,
  winnerCount: number
): string => {
  if (mode === 'Race') {
    return `${winnerCount} người đầu tiên đạt ${formatCa(targetAmount || 0)} Ca sẽ thắng`;
  }
  return 'Ai donate nhiều nhất khi hết giờ sẽ thắng';
};

/**
 * Câu luật cho trang sự kiện công khai (`/su-kien/:id`) — khác `challengeRuleSentence` (thẻ
 * trang cá nhân của MỘT creator) ở chỗ sự kiện tính donate cho BẤT KỲ creator nào, cần nói rõ.
 */
export const donateEventRuleSentence = (
  mode: ChallengeMode,
  targetAmount: number | null,
  winnerCount: number
): string => {
  if (mode === 'Race') {
    return `${winnerCount} người đầu tiên đạt tổng ${formatCa(targetAmount || 0)} Ca donate sẽ thắng.`;
  }
  return 'Ai donate tổng nhiều nhất cho bất kỳ creator nào trong thời gian sự kiện sẽ thắng.';
};

/** "Hạng 1" khi một hạng, "Hạng 2–3" khi cả một khoảng — bảng phần thưởng trang sự kiện. */
export const rewardRankLabel = (rankFrom: number, rankTo: number): string =>
  rankFrom === rankTo ? `Hạng ${rankFrom}` : `Hạng ${rankFrom}–${rankTo}`;
